import { BrandPlanTactic, ContentCount, ContentStrategyType } from 'types'

export type BusinessType = 'service' | 'product' | 'place'
export type Goal =
  | 'grow_audience'
  | 'generate_leads'
  | 'convert_clients'
  | 'drive_repeat_business'
  | 'build_product_awareness'
  | 'make_more_sales'
  | 'create_repeat_customers'
  | 'get_found_locally'
  | 'bring_in_new_customers'
  | 'keep_customers_coming_back'
  | 'grow_through_word_of_mouth'

export interface Strategy {
  id: string
  name: string
  description: string
  crosspost_contents: ContentCount[]
  video_crosspost_contents: ContentCount[]
  seo_contents: ContentCount[]
  email_contents: ContentCount[]
  tactics: BrandPlanTactic[]
  selected_goals: Goal[]
}

export const SOCIAL_TACTICS = [
  BrandPlanTactic.Instagram,
  BrandPlanTactic.Facebook,
  BrandPlanTactic.LinkedIn,
  BrandPlanTactic.Twitter,
] as const
export const VIDEO_TACTICS = [BrandPlanTactic.TikTok, BrandPlanTactic.YouTube, BrandPlanTactic.InstagramReel] as const

// Source: https://docs.google.com/spreadsheets/d/1G2-_jRocqV49xeIuigcvT93QdVoFL3pBTtl68RfODdA/edit?gid=1230899626#gid=1230899626
export const strategiesDefaultData: Record<BusinessType, { id: string; name: string; strategies: Strategy[] }> = {
  service: {
    id: 'service',
    name: 'Service-Based Businesses',
    strategies: [
      {
        id: ContentStrategyType.FounderStory,
        name: 'Founder Story',
        description:
          'Tell the story behind the brand, why it exists, and what it stands for. Best for building emotional connection and differentiation.',
        crosspost_contents: [{ type: 'post', count: 3 }],
        video_crosspost_contents: [{ type: 'post', count: 1 }],
        email_contents: [{ type: 'newsletter', count: 1 }],
        seo_contents: [{ type: 'blog_post', count: 0 }],
        selected_goals: ['grow_audience', 'generate_leads', 'convert_clients', 'drive_repeat_business'],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.Email,
        ],
      },
      {
        id: ContentStrategyType.EducationalContent,
        name: 'Educational Content',
        description:
          'Teach something useful about the broader category or problem space, not just your products. Best for reaching new audiences through value.',
        crosspost_contents: [{ type: 'post', count: 3 }],
        video_crosspost_contents: [{ type: 'post', count: 2 }],
        email_contents: [{ type: 'newsletter', count: 1 }],
        seo_contents: [{ type: 'blog_post', count: 1 }],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.SEO,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['grow_audience', 'generate_leads', 'convert_clients', 'drive_repeat_business'],
      },
      {
        id: ContentStrategyType.QuickTips,
        name: 'Quick Tips',
        description:
          'Offer focused, immediately applicable advice your audience can act on today. Best for demonstrating expertise and earning consistent engagement.',
        crosspost_contents: [{ type: 'post', count: 5 }],
        video_crosspost_contents: [{ type: 'post', count: 3 }],
        email_contents: [{ type: 'newsletter', count: 1 }],
        seo_contents: [{ type: 'blog_post', count: 0 }],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['grow_audience', 'generate_leads', 'convert_clients', 'drive_repeat_business'],
      },
      {
        id: ContentStrategyType.ThoughtLeadership,
        name: 'Thought Leadership',
        description:
          'Share original perspectives, industry insights, and expert takes on trends shaping your field. Best for building credibility and attracting high-intent prospects.',
        crosspost_contents: [{ type: 'post', count: 3 }],
        video_crosspost_contents: [{ type: 'post', count: 1 }],
        email_contents: [{ type: 'newsletter', count: 1 }],
        seo_contents: [{ type: 'blog_post', count: 0 }],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['grow_audience', 'generate_leads', 'convert_clients', 'drive_repeat_business'],
      },
    ],
  },
  product: {
    id: 'product',
    name: 'Product-Based Businesses',
    strategies: [
      {
        id: ContentStrategyType.FounderStory,
        name: 'Founder Story',
        description:
          'Tell the story behind the brand, why it exists, and what it stands for. Best for building emotional connection and differentiation.',
        crosspost_contents: [{ type: 'post', count: 3 }],
        video_crosspost_contents: [{ type: 'post', count: 2 }],
        email_contents: [{ type: 'newsletter', count: 1 }],
        seo_contents: [{ type: 'blog_post', count: 0 }],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['grow_audience', 'build_product_awareness', 'make_more_sales', 'create_repeat_customers'],
      },
      {
        id: ContentStrategyType.EducationalContent,
        name: 'Educational Content',
        description:
          'Teach something useful about the broader category or problem space, not just your products. Best for reaching new audiences through value.',
        crosspost_contents: [{ type: 'post', count: 3 }],
        video_crosspost_contents: [{ type: 'post', count: 1 }],
        email_contents: [{ type: 'newsletter', count: 1 }],
        seo_contents: [{ type: 'blog_post', count: 1 }],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.SEO,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['grow_audience', 'build_product_awareness', 'make_more_sales', 'create_repeat_customers'],
      },
      {
        id: ContentStrategyType.LifestyleContent,
        name: 'Lifestyle Content',
        description:
          "Show your products in the context of your customer's aspirations and daily life. Best for helping customers picture themselves using the product.",
        crosspost_contents: [{ type: 'post', count: 3 }],
        video_crosspost_contents: [{ type: 'post', count: 2 }],
        email_contents: [{ type: 'newsletter', count: 1 }],
        seo_contents: [{ type: 'blog_post', count: 0 }],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['grow_audience', 'build_product_awareness', 'make_more_sales', 'create_repeat_customers'],
      },
      {
        id: ContentStrategyType.ProblemSolution,
        name: 'Problem/Solution',
        description:
          'Show the specific problem your product solves in relatable, concrete terms. Best for moving shoppers from curiosity to consideration.',
        crosspost_contents: [{ type: 'post', count: 2 }],
        video_crosspost_contents: [{ type: 'post', count: 2 }],
        email_contents: [{ type: 'newsletter', count: 1 }],
        seo_contents: [{ type: 'blog_post', count: 1 }],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.SEO,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['grow_audience', 'build_product_awareness', 'make_more_sales', 'create_repeat_customers'],
      },
      {
        id: ContentStrategyType.HowToAndTutorials,
        name: 'How-To & Tutorials',
        description:
          'Demonstrate how to use, style, apply, or get the most from your products. Best for building confidence and reducing purchase friction.',
        crosspost_contents: [{ type: 'post', count: 2 }],
        video_crosspost_contents: [{ type: 'post', count: 3 }],
        email_contents: [{ type: 'newsletter', count: 1 }],
        seo_contents: [{ type: 'blog_post', count: 1 }],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.SEO,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['grow_audience', 'build_product_awareness', 'make_more_sales', 'create_repeat_customers'],
      },
    ],
  },
  place: {
    id: 'place',
    name: 'Place-Based Businesses',
    strategies: [
      {
        id: ContentStrategyType.OwnerStory,
        name: 'Owner Story',
        description:
          'Tell the story behind the brand, why it exists, and what it stands for. Best for building emotional connection and differentiation.',
        crosspost_contents: [{ type: 'post', count: 3 }],
        video_crosspost_contents: [{ type: 'post', count: 1 }],
        email_contents: [{ type: 'newsletter', count: 1 }],
        seo_contents: [{ type: 'blog_post', count: 0 }],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.Email,
        ],
        selected_goals: [
          'get_found_locally',
          'bring_in_new_customers',
          'keep_customers_coming_back',
          'grow_through_word_of_mouth',
        ],
      },
      {
        id: ContentStrategyType.ThoughtLeadership,
        name: 'Thought Leadership',
        description:
          'Share original perspectives, industry insights, and expert takes on trends shaping your field. Best for building credibility and attracting high-intent prospects.',
        crosspost_contents: [{ type: 'post', count: 3 }],
        video_crosspost_contents: [{ type: 'post', count: 1 }],
        email_contents: [{ type: 'newsletter', count: 1 }],
        seo_contents: [{ type: 'blog_post', count: 0 }],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.Email,
        ],
        selected_goals: [
          'get_found_locally',
          'bring_in_new_customers',
          'keep_customers_coming_back',
          'grow_through_word_of_mouth',
        ],
      },
      {
        id: ContentStrategyType.EducationalContent,
        name: 'Educational Content',
        description:
          'Teach something useful about the broader category or problem space, not just your products. Best for reaching new audiences through value.',
        crosspost_contents: [{ type: 'post', count: 3 }],
        video_crosspost_contents: [{ type: 'post', count: 1 }],
        email_contents: [{ type: 'newsletter', count: 1 }],
        seo_contents: [{ type: 'blog_post', count: 1 }],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.SEO,
          BrandPlanTactic.Email,
        ],
        selected_goals: [
          'get_found_locally',
          'bring_in_new_customers',
          'keep_customers_coming_back',
          'grow_through_word_of_mouth',
        ],
      },
      {
        id: ContentStrategyType.ProblemSolution,
        name: 'Problem/Solution',
        description:
          'Show the specific problem you solve for customers in relatable, concrete terms. Best for moving people from awareness to action.',
        crosspost_contents: [{ type: 'post', count: 2 }],
        video_crosspost_contents: [{ type: 'post', count: 1 }],
        email_contents: [{ type: 'newsletter', count: 1 }],
        seo_contents: [{ type: 'blog_post', count: 0 }],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.Email,
        ],
        selected_goals: [
          'get_found_locally',
          'bring_in_new_customers',
          'keep_customers_coming_back',
          'grow_through_word_of_mouth',
        ],
      },
    ],
  },
}
